import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ErrorBoundary } from 'react-error-boundary'
import { toast } from 'react-toastify'
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Printer,
  Save,
  GitCompare,
  Download,
  Mail,
  X
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useAuth } from '../../contexts/AuthContext'
import managementService from '../../services/managementService'
import useManagementReport from './useManagementReport'
import ManagementFiltersBar from './ManagementFiltersBar'
import {
  AutoSection,
  DataTable,
  DrillLinks,
  ErrorState,
  HealthBadge,
  KpiGrid,
  KpiTile,
  Loading,
  Section,
  Unavailable
} from './ManagementWidgets'
import { HealthSignals, KpiGroups } from './MisSnapshotReport'
import { PipelineSection, RevenueTrendChart, TOP_CUSTOMER_COLUMNS } from './SalesPipelineRevenueReport'
import { projectLink } from './ProjectPortfolioHealthReport'
import { EMPLOYEE_COLUMNS, ResourceCostTiles } from './ResourceCapacityReport'
import { AgeingBuckets, CUSTOMER_COLUMNS } from './CustomerCollectionHealthReport'
import { BudgetTrendChart, CATEGORY_COLUMNS } from './ExpenseBudgetPerformanceReport'
import { SUBSCRIPTION_COLUMNS } from './RenewalsCommitmentsReport'
import { ActionSummaryTiles, RiskSummaryTiles } from './RiskComplianceActionTracker'
import { ActionPanel, DecisionCommentPanel, GapsTable, RiskPanel } from './ManagementGovernancePanels'
import {
  EMAIL_DISABLED_NOTE,
  applyTextFilters,
  at,
  exportToSpreadsheet,
  flattenMetrics,
  formatDate,
  formatDateTime,
  getErrorMessage,
  isMasked,
  isMetric,
  kpiMeta,
  kpiTrend,
  metricText,
  toApiParams
} from './managementUtils'
import './Management.css'
import './DirectorsBusinessReview.css'

const R = '/management/reports'

export const DIRECTOR_REVIEW_SCREENS = [
  { order: 1, key: 'review_cover', title: 'Review Cover', to: null },
  { order: 2, key: 'executive_overview', title: 'Executive Overview', to: `${R}/mis-snapshot` },
  { order: 3, key: 'business_progress', title: 'Business Progress', to: `${R}/mis-snapshot` },
  { order: 4, key: 'sales_pipeline', title: 'Sales and Opportunity Pipeline', to: `${R}/sales-pipeline` },
  { order: 5, key: 'revenue_invoicing', title: 'Revenue and Invoicing', to: `${R}/sales-pipeline` },
  { order: 6, key: 'collections_receivables', title: 'Collections and Receivables', to: `${R}/customer-collection` },
  { order: 7, key: 'financial_health', title: 'Financial Health', to: `${R}/financial-health` },
  { order: 8, key: 'expense_analysis', title: 'Expense Analysis', to: `${R}/expense-budget` },
  { order: 9, key: 'project_portfolio', title: 'Project Portfolio', to: `${R}/project-portfolio` },
  { order: 10, key: 'project_delivery_profitability', title: 'Project Delivery and Profitability', to: `${R}/project-portfolio` },
  { order: 11, key: 'resource_overview', title: 'Resource Overview', to: `${R}/resource-capacity` },
  { order: 12, key: 'resource_allocation_capacity', title: 'Resource Allocation and Capacity', to: `${R}/resource-capacity` },
  { order: 13, key: 'customer_health', title: 'Customer Health', to: `${R}/customer-collection` },
  { order: 14, key: 'strategic_initiatives', title: 'Products and Strategic Initiatives', to: null },
  { order: 15, key: 'infrastructure_commitments', title: 'Infrastructure, Assets and Recurring Commitments', to: `${R}/renewals` },
  { order: 16, key: 'compliance_readiness', title: 'Compliance and Operational Readiness', to: `${R}/risk-actions` },
  { order: 17, key: 'key_risks', title: 'Key Risks', to: `${R}/risk-actions` },
  { order: 18, key: 'decisions_actions', title: 'Decisions and Actions', to: `${R}/risk-actions` }
]

const SCREEN_STATUS_LABEL = { actual: 'Live data', partial: 'Partial data', unavailable: 'Data unavailable' }
const SCREEN_STATUS_TONE = { actual: 'Green', partial: 'Amber', unavailable: 'Grey' }

// ---------- per-screen renderers ----------

const ReviewCover = ({ data, ctx }) => (
  <div className="dbr-cover">
    <p className="dbr-cover-eyebrow">{ctx?.companyName || 'Company'}</p>
    <h1>Director&apos;s Business Review</h1>
    <p className="dbr-cover-period">
      Reporting period: <strong>{data?.reportingPeriod?.label || '—'}</strong>
    </p>
    <p className="dbr-cover-asof">
      Prepared from ERP data as of {formatDateTime(data?.generatedAt || data?.reportingPeriod?.asOf)}
    </p>
    <div className="dbr-exec-health" style={{ marginTop: 24 }}>
      <span>Overall company health</span>
      <HealthBadge status={data?.overallHealth} label={data?.overallHealth || 'Grey'} />
    </div>
    <p className="dbr-cover-notice">{data?.confidentialNotice || 'Confidential management information.'}</p>
  </div>
)

const ExecutiveSummary = ({ data }) => (
  <>
    <div className="dbr-exec-health">
      <span>Overall business health</span>
      <HealthBadge status={data.overallHealth} label={data.overallHealth === 'Grey' ? 'Insufficient data' : data.overallHealth} />
    </div>
    <Section title="Health signals"><HealthSignals signals={data.healthSignals} /></Section>
    <KpiGroups kpis={data.kpis} />
  </>
)

const BusinessProgressScreen = ({ data: d }) => (
  <>
    <Section title="Period progress">
      <KpiGrid>
        <KpiTile label="Revenue invoiced" metric={at(d, 'revenue.invoicedTaxable')} format="currency" />
        <KpiTile label="Revenue growth" metric={at(d, 'revenue.growthPercent')} format="signedPercent" />
        <KpiTile label="Collections" metric={at(d, 'collections.received')} format="currency" />
        <KpiTile label="Burn (MTD)" metric={at(d, 'burn.monthToDate')} format="currency" />
        <KpiTile label="Active projects" metric={at(d, 'projects.active')} />
        <KpiTile label="Headcount" metric={at(d, 'headcount')} />
        <KpiTile label="Sales target" metric={d.salesTarget} format="currency" />
        <KpiTile label="Target achievement" metric={d.targetAchievementPercent} format="percent" />
      </KpiGrid>
    </Section>
    <Section title="Monthly revenue trend"><RevenueTrendChart trend={d.monthlyTrend} /></Section>
    <Section title="Achievements & setbacks">
      <Unavailable note={d.achievements?.note || d.setbacks?.note || 'Record management notes and completed milestones to populate this section.'} />
    </Section>
  </>
)

const FinancialHealthScreen = ({ data: d }) => (
  <>
    <Section title="Cash & runway">
      <KpiGrid>
        <KpiTile label="Cash (book)" metric={at(d, 'cash.bookBalance')} format="currency" />
        <KpiTile label="Verified cash" metric={at(d, 'cash.verifiedCash') || d.verifiedCash} format="currency" />
        <KpiTile label="Runway" metric={at(d, 'runway.months')} format="months" />
        <KpiTile label="Burn (30d)" metric={at(d, 'burn.trailing30Day')} format="currency" />
        <KpiTile label="Burn (MTD)" metric={at(d, 'burn.monthToDate')} format="currency" />
        <KpiTile label="Weighted pipeline (90d)" metric={at(d, 'pipeline.weighted90Day')} format="currency" />
      </KpiGrid>
    </Section>
    <div className="mis-two-col">
      <Section title="Receivables & collections">
        <KpiGrid>
          <KpiTile label="Outstanding" metric={at(d, 'receivables.outstanding')} format="currency" />
          <KpiTile label="Overdue %" metric={at(d, 'receivables.overduePercent')} format="percent" />
          <KpiTile label="Collected" metric={at(d, 'collections.received')} format="currency" />
        </KpiGrid>
      </Section>
      <Section title={`Budget${d.budget?.financialYear ? ` — FY ${d.budget.financialYear}` : ''}`}>
        <KpiGrid>
          <KpiTile label="Monthly budget" metric={at(d, 'budget.monthlyBudget')} format="currency" />
          <KpiTile label="MTD variance" metric={at(d, 'budget.varianceMonthToDatePercent')} format="signedPercent" />
          <KpiTile label="Credit notes" metric={d.creditNotes} format="currency" />
        </KpiGrid>
      </Section>
    </div>
    <Section title="Accounts">
      {isMasked(d.cash) ? <Unavailable note="Restricted — requires sensitive finance permission" /> : (
        <DataTable
          rows={d.cash?.accounts || []}
          emptyText="No active cash or bank accounts configured."
          columns={[{ key: 'name', label: 'Account' }, { key: 'type', label: 'Type' }, { key: 'bookBalance', label: 'Book balance', format: 'currency' }]}
        />
      )}
    </Section>
  </>
)

const RevenueScreen = ({ data: d, filters }) => (
  <>
    <Section title="Revenue">
      <KpiGrid>
        <KpiTile label="Invoiced (pre-tax)" metric={at(d, 'revenue.invoicedTaxable')} format="currency" />
        <KpiTile label="GST (period)" metric={at(d, 'revenue.gstAmount')} format="currency" />
        <KpiTile label="Growth" metric={at(d, 'revenue.growthPercent')} format="signedPercent" />
        <KpiTile label="Sales target" metric={d.salesTarget} format="currency" />
        <KpiTile label="Target achievement" metric={d.targetAchievementPercent} format="percent" />
        <KpiTile label="Credit notes" metric={d.creditNotes} format="currency" />
      </KpiGrid>
    </Section>
    <Section title="Invoices & GST — MTD / YTD">
      <KpiGrid>
        <KpiTile label="Invoices MTD" metric={at(d, 'revenue.mtd.invoiceCount')} />
        <KpiTile label="Invoiced MTD" metric={at(d, 'revenue.mtd.invoicedTaxable')} format="currency" />
        <KpiTile label="GST amount MTD" metric={at(d, 'revenue.mtd.gstAmount')} format="currency" />
        <KpiTile label="Invoices YTD" metric={at(d, 'revenue.ytd.invoiceCount')} />
        <KpiTile label="Invoiced YTD" metric={at(d, 'revenue.ytd.invoicedTaxable')} format="currency" />
        <KpiTile label="GST amount YTD" metric={at(d, 'revenue.ytd.gstAmount')} format="currency" />
      </KpiGrid>
    </Section>
    <Section title="Monthly trend"><RevenueTrendChart trend={d.monthlyTrend} /></Section>
    <Section title="Top customers">
      <DataTable rows={applyTextFilters(d.topCustomers, filters)} columns={TOP_CUSTOMER_COLUMNS} maxRows={10} />
    </Section>
  </>
)

const ConcentrationScreen = ({ data: d, filters }) => (
  <>
    <Section title="Concentration">
      <KpiGrid>
        <KpiTile label="Billed customers" metric={at(d, 'concentration.customerCount')} />
        <KpiTile label="Top customer share" metric={at(d, 'concentration.topCustomerSharePercent')} format="percent" />
        <KpiTile label="Top 5 share" metric={at(d, 'concentration.top5SharePercent')} format="percent" />
      </KpiGrid>
    </Section>
    <Section title="Top customers">
      <DataTable rows={applyTextFilters(d.topCustomers, filters)} columns={TOP_CUSTOMER_COLUMNS} maxRows={10} />
    </Section>
  </>
)

const ReceivablesScreen = ({ data: d, filters }) => (
  <>
    <Section title="Receivables">
      <KpiGrid>
        <KpiTile label="Outstanding" metric={at(d, 'summary.totalOutstanding')} format="currency" />
        <KpiTile label="Overdue" metric={at(d, 'summary.overdue')} format="currency" tone="amber" />
        <KpiTile label="90+ days" metric={at(d, 'summary.over90Days')} format="currency" tone="red" />
        <KpiTile label="DSO" metric={at(d, 'summary.dsoDays')} format="days" />
      </KpiGrid>
    </Section>
    <Section title="Ageing"><AgeingBuckets buckets={d.buckets} /></Section>
    <Section title="Customers"><DataTable rows={applyTextFilters(d.customers, filters)} columns={CUSTOMER_COLUMNS} maxRows={10} /></Section>
  </>
)

const CollectionsScreen = ({ data: d, filters }) => (
  <>
    <Section title="Collections">
      <KpiGrid>
        <KpiTile label="Received" metric={at(d, 'collections.received')} format="currency" />
        <KpiTile label="Cleared" metric={at(d, 'collections.cleared')} format="currency" />
        <KpiTile label="Billed in period" metric={at(d, 'collections.billedInPeriod')} format="currency" />
        <KpiTile label="Collection / billing" metric={at(d, 'collections.collectionToBillingPercent')} format="percent" />
        <KpiTile label="DSO" metric={d.dsoDays} format="days" />
        <KpiTile label="Credit notes" metric={d.creditNotes} format="currency" />
        <KpiTile label="Disputed invoices" metric={d.disputedInvoices} />
      </KpiGrid>
    </Section>
    <Section title="Top overdue customers">
      <DataTable rows={applyTextFilters(d.topOverdueCustomers, filters)} columns={CUSTOMER_COLUMNS} maxRows={10} emptyText="No overdue customers." />
    </Section>
  </>
)

const PortfolioScreen = ({ data: d, filters }) => {
  const s = d.summary || {}
  return (
    <>
      <Section title="Portfolio">
        <KpiGrid>
          <KpiTile label="Total projects" metric={s.totalProjects} />
          <KpiTile label="Active" metric={s.activeProjects} />
          <KpiTile label="Total SOW" metric={s.totalSow} format="currency" />
          <KpiTile label="Invoiced" metric={s.totalInvoicedTaxable} format="currency" />
          <KpiTile label="Outstanding" metric={s.totalOutstanding} format="currency" />
        </KpiGrid>
        <div className="mis-report-period" style={{ marginTop: 10 }}>
          {['Red', 'Amber', 'Green', 'Grey'].map((h) => <HealthBadge key={h} status={h} label={`${h}: ${s.byHealth?.[h] ?? 0}`} />)}
        </div>
      </Section>
      <Section title="Projects">
        <DataTable
          rows={applyTextFilters(d.projects, filters)}
          maxRows={12}
          columns={[
            { key: 'projectName', label: 'Project', render: projectLink },
            { key: 'customerName', label: 'Customer' },
            { key: 'projectManager', label: 'PM', render: (v) => v?.name || <span className="mis-muted">Not assigned</span> },
            { key: 'derivedHealth', label: 'Health', type: 'health' },
            { key: 'sowValue', label: 'SOW', format: 'currency' },
            { key: 'invoicedTaxable', label: 'Invoiced', format: 'currency' },
            { key: 'unbilledSow', label: 'Unbilled', format: 'currency' }
          ]}
        />
      </Section>
    </>
  )
}

const DeliveryScreen = ({ data: d, filters }) => (
  <>
    <Section title="Delivery">
      <KpiGrid>
        <KpiTile label="Overdue projects" metric={d.overdueProjects} tone="red" />
        <KpiTile label="Without end date" metric={d.projectsWithoutEndDate} tone="amber" />
        <KpiTile label="Milestones" metric={at(d, 'milestones.total')} />
        <KpiTile label="Completed" metric={at(d, 'milestones.completed')} />
        <KpiTile label="Overdue milestones" metric={at(d, 'milestones.overdue')} tone="amber" />
        <KpiTile label="Completion" metric={at(d, 'milestones.completionPercent')} format="percent" />
      </KpiGrid>
    </Section>
    <Section title="Active projects">
      <DataTable
        rows={applyTextFilters(d.projects, filters)}
        maxRows={12}
        columns={[
          { key: 'projectName', label: 'Project', render: projectLink },
          { key: 'derivedHealth', label: 'Health', type: 'health' },
          { key: 'plannedEndDate', label: 'Planned end', type: 'date' },
          { key: 'scheduleOverdue', label: 'Late', render: (v) => (v ? <HealthBadge status="Red" label="Late" /> : '—') },
          { key: 'milestones', label: 'Milestones overdue', render: (v) => metricText(v?.overdue) },
          { key: 'hoursLoggedInPeriod', label: 'Hours (period)', format: 'hours' }
        ]}
      />
    </Section>
  </>
)

const ProfitabilityScreen = ({ data: d, filters }) => (
  <>
    <Section title="Profitability">
      <KpiGrid>
        <KpiTile label="Costed projects" metric={d.costedProjects} />
        <KpiTile label="Portfolio cost" metric={d.portfolioCost} format="currency" />
        <KpiTile label="Portfolio margin" metric={d.portfolioMargin} format="currency" />
      </KpiGrid>
    </Section>
    <Section title="By project">
      <DataTable
        rows={applyTextFilters(d.projects, filters)}
        maxRows={12}
        columns={[
          { key: 'projectName', label: 'Project', render: projectLink },
          { key: 'invoicedTaxable', label: 'Invoiced', format: 'currency' },
          { key: 'projectCost', label: 'Cost', format: 'currency' },
          { key: 'margin', label: 'Margin', format: 'currency' },
          { key: 'marginPercent', label: 'Margin %', format: 'percent' }
        ]}
      />
    </Section>
  </>
)

const CapacityScreen = ({ data: d }) => {
  const s = d.summary || {}
  return (
    <>
      <Section title="Capacity">
        <KpiGrid>
          <KpiTile label="Headcount" metric={s.headcount} />
          <KpiTile label="Allocated" metric={s.allocatedEmployees} />
          <KpiTile label="Bench" metric={s.benchEmployees} tone="amber" />
          <KpiTile label="Over-allocated" metric={s.overAllocated} tone="red" />
          <KpiTile label="Under-allocated" metric={s.underAllocated} tone="amber" />
          <KpiTile label="Avg allocation" metric={s.averageAllocationPercent} format="percent" />
          <ResourceCostTiles resourceCost={d.resourceCost} />
        </KpiGrid>
      </Section>
      <Section title="By department">
        <DataTable
          rows={d.byDepartment || []}
          columns={[
            { key: 'department', label: 'Department' },
            { key: 'headcount', label: 'Headcount', format: 'number' },
            { key: 'benchEmployees', label: 'Bench', format: 'number' },
            { key: 'averageAllocationPercent', label: 'Avg allocation', format: 'percent' },
            { key: 'utilizationPercent', label: 'Utilisation', format: 'percent' }
          ]}
        />
      </Section>
      <Section title="Employees"><DataTable rows={d.employees || []} columns={EMPLOYEE_COLUMNS} maxRows={10} /></Section>
    </>
  )
}

const UtilizationScreen = ({ data: d }) => {
  const s = d.summary || {}
  const b = d.capacityBasis || {}
  return (
    <Section
      title="Utilisation"
      subtitle={b.workingDays != null ? `${b.workingDays} working days × ${b.hoursPerDay} h · time source: ${b.timeSource}` : undefined}
    >
      <KpiGrid>
        <KpiTile label="Capacity hours" metric={s.capacityHours} format="hours" />
        <KpiTile label="Hours logged" metric={s.hoursLogged} format="hours" />
        <KpiTile label="Billable hours" metric={s.billableHours} format="hours" />
        <KpiTile label="Utilisation" metric={s.utilizationPercent} format="percent" />
        <KpiTile label="Billable utilisation" metric={s.billableUtilizationPercent} format="percent" />
        <KpiTile label="Timesheet compliance" metric={s.timesheetCompliancePercent} format="percent" />
      </KpiGrid>
    </Section>
  )
}

const ExpenseScreen = ({ data: d }) => {
  const burnMasked = isMasked(d.burn)
  return (
    <>
      <Section title="Budget vs burn">
        <KpiGrid>
          {isMetric(d.budget)
            ? <KpiTile label="Approved budget" metric={d.budget} format="currency" />
            : (
              <>
                <KpiTile label="Annual budget" metric={d.budget?.totalAnnualBudget} format="currency" />
                <KpiTile label="Budget to date" metric={d.budget?.budgetToDate} format="currency" />
              </>
            )}
          <KpiTile label="FY to date actual" metric={at(d, 'burn.fyToDateActual')} format="currency" />
          <KpiTile label="Variance %" metric={at(d, 'burn.variancePercentToDate')} format="signedPercent" />
          <KpiTile label="Utilisation" metric={at(d, 'burn.budgetUtilisationPercent')} format="percent" />
          <KpiTile label="Over-budget categories" metric={at(d, 'burn.overBudgetCategories')} tone="red" />
          <KpiTile label="Project expenses" metric={at(d, 'projectExpenses.approvedInPeriod')} format="currency" />
        </KpiGrid>
      </Section>
      <Section title="Monthly trend">
        {burnMasked ? <Unavailable note="Restricted" /> : <BudgetTrendChart trend={d.burn?.monthlyTrend} />}
      </Section>
      <Section title="By category">
        {burnMasked ? <Unavailable note="Restricted" /> : <DataTable rows={d.burn?.byCategory || []} columns={CATEGORY_COLUMNS} maxRows={10} />}
      </Section>
    </>
  )
}

const RenewalsScreen = ({ data: d }) => {
  const s = d.subscriptions || {}
  const r = s.renewals || {}
  const cc = d.customerContracts || {}
  return (
    <>
      <Section title="Commitments & renewals">
        <KpiGrid>
          <KpiTile label="Active subscriptions" metric={s.activeCount} />
          <KpiTile label="Monthly commitment" metric={s.monthlyCommitmentInr} format="currency" />
          <KpiTile label="Annual commitment" metric={s.annualCommitmentInr} format="currency" />
          <KpiTile label="Overdue renewals" metric={at(r, 'overdue.count')} tone="red" />
          <KpiTile label="Due ≤30 days" metric={at(r, 'within30Days.count')} tone="amber" />
          <KpiTile label="Due 31–90 days" metric={{ value: (at(r, 'within31to60Days.count')?.value || 0) + (at(r, 'within61to90Days.count')?.value || 0), status: 'actual' }} />
          <KpiTile label="Contract renewals pending" metric={cc.pendingRenewals} />
          <KpiTile label="Contract renewal value" metric={cc.renewalValue} format="currency" />
        </KpiGrid>
      </Section>
      <Section title="Upcoming renewals"><DataTable rows={s.upcoming || []} columns={SUBSCRIPTION_COLUMNS} maxRows={10} emptyText="No renewals due in the next 90 days." /></Section>
    </>
  )
}

const RISK_PRINT_COLUMNS = [
  { key: 'title', label: 'Risk' },
  { key: 'category', label: 'Category' },
  { key: 'severity', label: 'Severity', type: 'health' },
  { key: 'owner', label: 'Owner', render: (v) => v?.name || 'Unassigned' },
  { key: 'targetDate', label: 'Target', type: 'date' },
  { key: 'status', label: 'Status' },
  { key: 'mitigation', label: 'Mitigation' }
]

const ACTION_PRINT_COLUMNS = [
  { key: 'description', label: 'Action' },
  { key: 'owner', label: 'Owner', render: (v) => v?.name || 'Unassigned' },
  { key: 'dueDate', label: 'Due', type: 'date' },
  { key: 'priority', label: 'Priority' },
  { key: 'effectiveStatus', label: 'Status', render: (v, r) => v || r.status }
]

const NOTE_PRINT_COLUMNS = (textKey) => [
  { key: textKey, label: textKey === 'decision' ? 'Decision' : 'Comment' },
  { key: 'createdBy', label: 'By', render: (v) => v?.name || '—' },
  { key: 'createdAt', label: 'Date', type: 'date' }
]

const RiskScreen = ({ data, ctx }) => (
  <>
    <Section title="Risk summary"><RiskSummaryTiles risks={data} /></Section>
    <Section title="Risk register" subtitle="Add risks or update their status during the review">
      {ctx.printing
        ? <DataTable rows={data.items || []} columns={RISK_PRINT_COLUMNS} maxRows={100} emptyText="No risks recorded." />
        : <RiskPanel can={ctx.can} onChanged={ctx.refresh} sourceModule="DIRECTOR_REVIEW" />}
    </Section>
  </>
)

const ActionScreen = ({ data, ctx }) => (
  <>
    <Section title="Action summary"><ActionSummaryTiles actions={data} /></Section>
    <Section title="Actions" subtitle="Create, start and close management actions">
      {ctx.printing
        ? <DataTable rows={data.items || []} columns={ACTION_PRINT_COLUMNS} maxRows={100} emptyText="No actions recorded." />
        : <ActionPanel can={ctx.can} onChanged={ctx.refresh} sourceReport="directors-review" reviewSnapshotId={ctx.snapshotId} />}
    </Section>
  </>
)

const GovernanceScreen = ({ data, ctx }) => (
  <>
    <Section title="Compliance gaps" subtitle={`${data?.gapCount?.value ?? 0} checks need attention`}>
      <GapsTable gaps={data?.gaps} />
    </Section>
    <Section title="Decisions & comments" subtitle="Recorded for the selected review period">
      {ctx.printing ? (
        <div className="mis-two-col">
          <DataTable rows={data?.decisions || []} columns={NOTE_PRINT_COLUMNS('decision')} maxRows={100} emptyText="No decisions recorded." />
          <DataTable rows={data?.comments || []} columns={NOTE_PRINT_COLUMNS('comment')} maxRows={100} emptyText="No comments recorded." />
        </div>
      ) : (
        <DecisionCommentPanel can={ctx.can} period={ctx.period} reviewSnapshotId={ctx.snapshotId} />
      )}
    </Section>
    {!ctx.printing && (
      <Section title="Follow-up actions">
        <ActionPanel can={ctx.can} onChanged={ctx.refresh} sourceReport="directors-review" reviewSnapshotId={ctx.snapshotId} />
      </Section>
    )}
  </>
)

const CollectionsReceivablesScreen = ({ data: d, filters }) => (
  <>
    <ReceivablesScreen data={d} filters={filters} />
    <CollectionsScreen data={d} filters={filters} />
  </>
)

const DeliveryProfitabilityScreen = ({ data: d, filters }) => (
  <>
    <DeliveryScreen data={d} filters={filters} />
    <ProfitabilityScreen data={d} filters={filters} />
  </>
)

const CustomerHealthScreen = ({ data: d, filters }) => (
  <>
    <ConcentrationScreen data={d} filters={filters} />
    <Section title="Collection health">
      <KpiGrid>
        <KpiTile label="Outstanding" metric={at(d, 'summary.totalOutstanding')} format="currency" />
        <KpiTile label="Overdue" metric={at(d, 'summary.overdue')} format="currency" tone="amber" />
        <KpiTile label="DSO" metric={at(d, 'summary.dsoDays')} format="days" />
      </KpiGrid>
    </Section>
    <Section title="Overdue customers">
      <DataTable rows={applyTextFilters(d.topOverdueCustomers || d.customers, filters)} columns={CUSTOMER_COLUMNS} maxRows={10} emptyText="No overdue customers." />
    </Section>
  </>
)

const InitiativesScreen = ({ data: d }) => {
  const rows = Array.isArray(d?.initiatives) ? d.initiatives : null
  return (
    <Section title="Products and strategic initiatives">
      {!rows || !rows.length ? (
        <Unavailable note={d?.initiatives?.note || 'No Initiative records found for this company.'} />
      ) : (
        <DataTable
          rows={rows}
          columns={[
            { key: 'name', label: 'Initiative' },
            { key: 'owner', label: 'Owner' },
            { key: 'status', label: 'Status' },
            { key: 'progress', label: 'Progress' },
            { key: 'dueDate', label: 'Target', type: 'date' }
          ]}
        />
      )}
    </Section>
  )
}

const DecisionsActionsScreen = ({ data, ctx }) => (
  <>
    <ActionScreen data={data?.actions || data} ctx={ctx} />
    <GovernanceScreen data={data} ctx={ctx} />
  </>
)

const RENDERERS = {
  review_cover: ReviewCover,
  executive_overview: ExecutiveSummary,
  business_progress: BusinessProgressScreen,
  sales_pipeline: ({ data }) => <Section title="Pipeline"><PipelineSection pipeline={data?.pipeline} /></Section>,
  revenue_invoicing: RevenueScreen,
  collections_receivables: CollectionsReceivablesScreen,
  financial_health: FinancialHealthScreen,
  expense_analysis: ExpenseScreen,
  project_portfolio: PortfolioScreen,
  project_delivery_profitability: DeliveryProfitabilityScreen,
  resource_overview: CapacityScreen,
  resource_allocation_capacity: UtilizationScreen,
  customer_health: CustomerHealthScreen,
  strategic_initiatives: InitiativesScreen,
  infrastructure_commitments: RenewalsScreen,
  compliance_readiness: ({ data, ctx }) => (
    <Section title="Compliance gaps" subtitle={`${data?.gapCount?.value ?? 0} checks need attention`}>
      <GapsTable gaps={data?.gaps} />
      {!ctx?.printing && <p className="mis-muted">Detailed GST working remains in Settings → GST Reminder.</p>}
    </Section>
  ),
  key_risks: RiskScreen,
  decisions_actions: DecisionsActionsScreen
}

const INTERACTIVE_SCREENS = new Set(['key_risks', 'decisions_actions', 'compliance_readiness', 'review_cover'])

const ScreenBody = ({ screen, ctx, filters }) => {
  const Renderer = RENDERERS[screen.key]
  if (!screen.data && !INTERACTIVE_SCREENS.has(screen.key)) {
    return (
      <Section title="Data unavailable">
        <Unavailable note="This screen was not returned for the selected period." />
      </Section>
    )
  }
  const data = screen.data || {}
  const fallback = <Section title="Details"><AutoSection data={data} /></Section>
  if (!Renderer) return fallback
  return (
    <ErrorBoundary fallback={fallback} resetKeys={[screen.data]}>
      <Renderer data={data} ctx={ctx} filters={filters} />
    </ErrorBoundary>
  )
}

const Screen = ({ screen, index, total, ctx, filters }) => (
  <article className="dbr-screen">
    <header className="dbr-screen-header">
      <div>
        <div className="dbr-screen-count">Screen {index + 1} of {total}</div>
        <h2>{screen.title}</h2>
      </div>
      <div className="dbr-screen-meta">
        <HealthBadge status={SCREEN_STATUS_TONE[screen.status] || 'Grey'} label={SCREEN_STATUS_LABEL[screen.status] || 'Data unavailable'} />
        {screen.unavailableMetrics?.length > 0 && (
          <span className="mis-muted" title={screen.unavailableMetrics.join('\n')}>
            {screen.unavailableMetrics.length} metric{screen.unavailableMetrics.length === 1 ? '' : 's'} unavailable
          </span>
        )}
      </div>
    </header>
    {screen.to && <DrillLinks links={[{ label: 'Open detailed report', to: screen.to }]} />}
    <ScreenBody screen={screen} ctx={ctx} filters={filters} />
  </article>
)

// ---------- snapshots & compare ----------

const TREND_TONE = { improved: 'Green', deteriorated: 'Red', unchanged: 'Grey', changed: 'Amber', unavailable: 'Grey' }

const ComparePanel = ({ snapshots, loadingSnapshots, apiParams, onClose }) => {
  const [baseId, setBaseId] = useState('')
  const [compareId, setCompareId] = useState('')
  const [result, setResult] = useState(null)
  const [running, setRunning] = useState(false)

  useEffect(() => {
    if (!baseId && snapshots.length) setBaseId(snapshots[0]._id)
  }, [snapshots, baseId])

  const run = async () => {
    if (!baseId) return toast.warn('Select a saved snapshot to compare against')
    setRunning(true)
    try {
      const res = await managementService.compareDirectorReviewSnapshots({
        baseSnapshotId: baseId,
        compareSnapshotId: compareId || undefined,
        filters: compareId ? undefined : apiParams
      })
      setResult(res?.data || null)
    } catch (err) {
      if (!err?._errorHandled) toast.error(getErrorMessage(err, 'Comparison failed'))
    } finally {
      setRunning(false)
    }
  }

  const rows = useMemo(() => Object.entries(result?.kpis || {}).map(([key, diff]) => ({
    key,
    label: kpiMeta(key).label,
    format: kpiMeta(key).format,
    base: diff?.base,
    compare: diff?.compare,
    delta: diff?.delta,
    deltaPercent: diff?.deltaPercent,
    trend: kpiTrend(key, diff)
  })), [result])

  const counts = rows.reduce((acc, r) => ({ ...acc, [r.trend]: (acc[r.trend] || 0) + 1 }), {})
  const label = (side) => (side?.source === 'live' ? 'Live (now)' : `${side?.reportingPeriod?.label || 'Snapshot'} · ${formatDate(side?.asOf)}`)

  return (
    <Section
      title="Compare with a previous review"
      className="no-print dbr-panel"
      actions={<button type="button" className="btn btn-sm btn-link" onClick={onClose}><X size={14} /> Close</button>}
    >
      {loadingSnapshots ? <Loading label="Loading snapshots…" /> : !snapshots.length ? (
        <Unavailable>No saved snapshots yet. Save a snapshot first, then compare future reviews against it.</Unavailable>
      ) : (
        <>
          <div className="mis-form">
            <div>
              <label>Previous snapshot (base)</label>
              <select className="form-select form-select-sm" value={baseId} onChange={(e) => setBaseId(e.target.value)}>
                {snapshots.map((s) => (
                  <option key={s._id} value={s._id}>{s.reportingPeriod?.label || 'Snapshot'} · {formatDate(s.asOf)} · {s.overallHealth}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Compare to</label>
              <select className="form-select form-select-sm" value={compareId} onChange={(e) => setCompareId(e.target.value)}>
                <option value="">Live data (now)</option>
                {snapshots.filter((s) => s._id !== baseId).map((s) => (
                  <option key={s._id} value={s._id}>{s.reportingPeriod?.label || 'Snapshot'} · {formatDate(s.asOf)}</option>
                ))}
              </select>
            </div>
            <div className="mis-form-actions">
              <button type="button" className="btn btn-sm btn-primary" onClick={run} disabled={running}>{running ? 'Comparing…' : 'Compare'}</button>
            </div>
          </div>

          {result && (
            <>
              <div className="dbr-compare-head">
                <span>Base: <strong>{label(result.base)}</strong> <HealthBadge status={result.base?.overallHealth} /></span>
                <span>→</span>
                <span>Compare: <strong>{label(result.compare)}</strong> <HealthBadge status={result.compare?.overallHealth} /></span>
                {result.healthChanged && <HealthBadge status="Amber" label="Overall health changed" />}
              </div>
              <div className="mis-report-period" style={{ marginBottom: 10 }}>
                <HealthBadge status="Green" label={`Improved: ${counts.improved || 0}`} />
                <HealthBadge status="Red" label={`Deteriorated: ${counts.deteriorated || 0}`} />
                <HealthBadge status="Grey" label={`Unchanged: ${counts.unchanged || 0}`} />
                {counts.changed ? <HealthBadge status="Amber" label={`Changed: ${counts.changed}`} /> : null}
                <HealthBadge status="Grey" label={`Not comparable: ${counts.unavailable || 0}`} />
              </div>
              <DataTable
                rows={rows}
                rowKey={(r) => r.key}
                maxRows={40}
                columns={[
                  { key: 'label', label: 'KPI' },
                  { key: 'base', label: 'Base', render: (v, r) => metricText(v, r.format) },
                  { key: 'compare', label: 'Compare', render: (v, r) => metricText(v, r.format) },
                  { key: 'delta', label: 'Change', align: 'right', render: (v, r) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${metricText({ value: v, status: 'actual' }, r.format === 'signedPercent' ? 'percent' : r.format)}`) },
                  { key: 'deltaPercent', label: 'Change %', align: 'right', render: (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v}%`) },
                  { key: 'trend', label: 'Trend', render: (v) => <HealthBadge status={TREND_TONE[v]} label={v === 'unavailable' ? 'Not comparable' : v.charAt(0).toUpperCase() + v.slice(1)} /> }
                ]}
              />
            </>
          )}
        </>
      )}
    </Section>
  )
}

// ---------- main ----------

const isTypingTarget = (el) => {
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

const DirectorsBusinessReview = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const currentCompany = useSelector((state) => state.company?.currentCompany)
  const report = useManagementReport(managementService.getDirectorReview)
  const { data: bundle, filters, setFilters, loading, error, lastRefresh, completeness, reload, can } = report

  const [index, setIndex] = useState(0)
  const [presenting, setPresenting] = useState(false)
  const [printAll, setPrintAll] = useState(false)
  const [showCompare, setShowCompare] = useState(false)
  const [snapshots, setSnapshots] = useState([])
  const [loadingSnapshots, setLoadingSnapshots] = useState(false)
  const [savingSnapshot, setSavingSnapshot] = useState(false)
  const [lastSnapshotId, setLastSnapshotId] = useState(null)
  const deckRef = useRef(null)

  const companyName = currentCompany?.companyName
    || user?.companyName
    || user?.company?.companyName
    || user?.company?.name
    || 'Company'

  const screens = useMemo(() => {
    const fromApi = Array.isArray(bundle?.screens) ? bundle.screens : []
    const byKey = new Map(fromApi.map((s) => [s.key, s]))
    const known = DIRECTOR_REVIEW_SCREENS.map((def) => {
      const s = byKey.get(def.key)
      return s ? { ...def, ...s, title: s.title || def.title } : { ...def, status: 'unavailable', unavailableMetrics: [], data: null }
    })
    const extras = fromApi
      .filter((s) => !DIRECTOR_REVIEW_SCREENS.some((d) => d.key === s.key))
      .sort((a, b) => (a.order || 0) - (b.order || 0))
    return [...known, ...extras]
  }, [bundle])

  const total = screens.length
  const current = screens[Math.min(index, total - 1)]
  const apiParams = useMemo(() => toApiParams(filters), [filters])

  const go = useCallback((delta) => setIndex((i) => Math.max(0, Math.min(total - 1, i + delta))), [total])

  const loadSnapshots = useCallback(async () => {
    setLoadingSnapshots(true)
    try {
      const res = await managementService.listDirectorReviewSnapshots({ limit: 50 })
      setSnapshots(res?.data || [])
    } catch {
      setSnapshots([])
    } finally {
      setLoadingSnapshots(false)
    }
  }, [])

  useEffect(() => { loadSnapshots() }, [loadSnapshots])

  // ----- presentation mode -----
  const enterPresentation = useCallback(() => {
    setPresenting(true)
    const el = deckRef.current
    if (el?.requestFullscreen && !document.fullscreenElement) {
      el.requestFullscreen().catch(() => { /* CSS presentation mode still applies */ })
    }
  }, [])

  const exitPresentation = useCallback(() => {
    setPresenting(false)
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {})
  }, [])

  useEffect(() => {
    const onFsChange = () => { if (!document.fullscreenElement) setPresenting(false) }
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (isTypingTarget(e.target) || e.ctrlKey || e.metaKey || e.altKey) return
      if (e.key === 'ArrowRight' || e.key === 'PageDown') { e.preventDefault(); go(1) }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); go(-1) }
      else if (e.key === 'Home') { e.preventDefault(); setIndex(0) }
      else if (e.key === 'End') { e.preventDefault(); setIndex(total - 1) }
      else if (e.key === 'f' || e.key === 'F') { e.preventDefault(); presenting ? exitPresentation() : enterPresentation() }
      else if (e.key === 'Escape' && presenting) exitPresentation()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, total, presenting, enterPresentation, exitPresentation])

  useEffect(() => {
    if (deckRef.current) deckRef.current.scrollTop = 0
  }, [index])

  // ----- print -----
  useEffect(() => {
    if (!printAll) return undefined
    const t = setTimeout(() => {
      window.print()
      setPrintAll(false)
    }, 150)
    return () => clearTimeout(t)
  }, [printAll])

  // ----- snapshot -----
  const saveSnapshot = async () => {
    setSavingSnapshot(true)
    try {
      const res = await managementService.saveDirectorReviewSnapshot({ ...apiParams, label: bundle?.period?.label })
      if (res?.success) {
        setLastSnapshotId(res.data?._id || null)
        toast.success('Review snapshot saved')
        loadSnapshots()
      }
    } catch (err) {
      if (!err?._errorHandled) toast.error(getErrorMessage(err, 'Could not save snapshot'))
    } finally {
      setSavingSnapshot(false)
    }
  }

  const onExport = () => {
    if (!bundle) return
    const sheets = {
      'Executive KPIs': Object.entries(bundle.kpis || {}).map(([k, m]) => ({
        KPI: kpiMeta(k).label, Value: metricText(m, kpiMeta(k).format), Status: m?.status || 'missing', Note: m?.note || ''
      })),
      'Health signals': (bundle.healthSignals || []).map((s) => ({ Signal: s.label, Status: s.status }))
    }
    screens.forEach((s) => {
      if (s.key === 'executive_overview' || s.key === 'review_cover' || !s.data) return
      sheets[`${s.order || ''} ${s.title}`.trim()] = flattenMetrics(s.data)
    })
    exportToSpreadsheet('directors-business-review', sheets)
  }

  const ctx = {
    can,
    period: bundle?.period,
    snapshotId: lastSnapshotId,
    printing: printAll,
    refresh: () => reload({ silent: true }),
    companyName
  }

  const breadcrumbItems = [
    { label: 'Management', path: '/management?tab=reports', onClick: () => navigate('/management?tab=reports') },
    { label: "Director's Business Review", isCurrent: true }
  ]

  return (
    <div className="sales-dashboard management-report dbr-page">
      <div className="breadcrumb-section no-print">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="mis-report-header no-print">
        <div>
          <h1>Director&apos;s Business Review</h1>
          <p>{companyName} · board-ready review built from live ERP data</p>
        </div>
        <div className="mis-report-actions">
          <button type="button" className="btn btn-sm btn-primary" onClick={enterPresentation} disabled={!bundle} title="Presentation mode (F)">
            <Maximize2 size={14} /> Present
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPrintAll(true)} disabled={!bundle} title="Print all screens or save as PDF">
            <Printer size={14} /> Print / PDF
          </button>
          {can('EXPORT') && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onExport} disabled={!bundle}>
              <Download size={14} /> Export
            </button>
          )}
          {can('SAVE_SNAPSHOT') && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={saveSnapshot} disabled={!bundle || savingSnapshot}>
              <Save size={14} /> {savingSnapshot ? 'Saving…' : 'Save snapshot'}
            </button>
          )}
          <button type="button" className={`btn btn-sm ${showCompare ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setShowCompare((s) => !s)}>
            <GitCompare size={14} /> Compare
          </button>
          <span className="mis-email-disabled" title={EMAIL_DISABLED_NOTE}>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled aria-label={EMAIL_DISABLED_NOTE}><Mail size={14} /> Email</button>
          </span>
        </div>
      </div>

      <ManagementFiltersBar
        filters={filters}
        onChange={setFilters}
        onRefresh={() => reload()}
        lastRefresh={lastRefresh}
        completeness={completeness}
        loading={loading}
      />

      {showCompare && (
        <ComparePanel snapshots={snapshots} loadingSnapshots={loadingSnapshots} apiParams={apiParams} onClose={() => setShowCompare(false)} />
      )}

      {loading && !bundle && <Loading label="Preparing director review…" />}
      {!loading && error && <ErrorState message={error} onRetry={() => reload()} />}

      {bundle && (
        <div ref={deckRef} className={`dbr-deck ${presenting ? 'presenting' : ''} ${printAll ? 'print-all' : ''} ${loading ? 'mis-refreshing' : ''}`}>
          <div className="dbr-deck-bar">
            <div className="dbr-deck-title">
              <strong>{companyName}</strong>
              <span>{bundle.period?.label}</span>
              <span>As of {formatDate(bundle.period?.asOf)}</span>
              <span>Overall <HealthBadge status={bundle.overallHealth} /></span>
              <span className="print-only">Generated {formatDateTime(bundle.generatedAt || lastRefresh)}</span>
            </div>
            <div className="dbr-deck-nav no-print">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => go(-1)} disabled={index === 0} aria-label="Previous screen">
                <ChevronLeft size={16} />
              </button>
              <select
                className="form-select form-select-sm dbr-jump"
                value={index}
                onChange={(e) => setIndex(Number(e.target.value))}
                aria-label="Jump to screen"
              >
                {screens.map((s, i) => (
                  <option key={s.key} value={i}>
                    {i + 1}. {s.title}{s.status === 'unavailable' ? ' (no data)' : s.status === 'partial' ? ' (partial)' : ''}
                  </option>
                ))}
              </select>
              <span className="dbr-counter">{index + 1} / {total}</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => go(1)} disabled={index >= total - 1} aria-label="Next screen">
                <ChevronRight size={16} />
              </button>
              {presenting ? (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={exitPresentation} title="Exit (Esc)"><Minimize2 size={14} /></button>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={enterPresentation} title="Present (F)"><Maximize2 size={14} /></button>
              )}
            </div>
          </div>

          <div className="dbr-progress no-print" aria-hidden="true">
            {screens.map((s, i) => (
              <button
                key={s.key}
                type="button"
                className={`dbr-dot ${i === index ? 'active' : ''} dbr-dot-${s.status || 'unavailable'}`}
                onClick={() => setIndex(i)}
                title={`${i + 1}. ${s.title}`}
                tabIndex={-1}
              />
            ))}
          </div>

          {printAll
            ? screens.map((s, i) => <Screen key={s.key} screen={s} index={i} total={total} ctx={ctx} filters={filters} />)
            : current && <Screen key={current.key} screen={current} index={index} total={total} ctx={ctx} filters={filters} />}

          <div className="dbr-hint no-print">← → to navigate · F for presentation · Esc to exit</div>
        </div>
      )}
    </div>
  )
}

export default DirectorsBusinessReview
